const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---- Auth token (Bearer) storage ----
// Installed standalone PWAs (iOS especially) block the cross-site SameSite=None
// session cookie, so we ALSO keep the session token client-side and send it as
// `Authorization: Bearer`. The backend accepts cookie OR bearer, so web keeps using
// the cookie and the PWA falls back to the header — auth works reliably on both.
const TOKEN_KEY = "cheapify_session_token";
export function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); } catch { /* storage unavailable */ }
}
export function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* storage unavailable */ }
}

// Which client this request comes from, for the single-session device lock.
export function clientType() {
  try {
    const pwa =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator?.standalone === true;
    return pwa ? "PWA" : "WEB";
  } catch {
    return "WEB";
  }
}

// Standard headers for every API call: client type, bearer token (if stored), + extras.
function authHeaders(extra = {}) {
  const h = { "X-Client-Type": clientType(), ...extra };
  try {
    h["X-App-Platform"] = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone ? "pwa" : "web_browser";
  } catch { /* noop */ }
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) h.Authorization = `Bearer ${t}`;
  } catch { /* storage unavailable */ }
  return h;
}

// Create a Stripe checkout session. The backend defines all prices server-side;
// we only send the chosen tier + the current origin for success/cancel URLs.
export async function createCheckout(payload) {
  const res = await fetch(`${API}/checkout`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ...payload, origin_url: window.location.origin }),
  });
  return handle(res);
}

// Poll the status of a checkout session after returning from Stripe.
export async function getCheckoutStatus(sessionId) {
  const res = await fetch(`${API}/checkout/status/${sessionId}`, { credentials: "include", headers: authHeaders() });
  return handle(res);
}

// Fetch the current user's successful purchase records.
export async function getCheckoutHistory() {
  const res = await fetch(`${API}/checkout/history`, { credentials: "include", headers: authHeaders() });
  return handle(res);
}

// Global promo bar settings (public read).
export async function getPromo() {
  const res = await fetch(`${API}/settings/promo`, { credentials: "include", headers: authHeaders() });
  return handle(res);
}

// Admin-only: toggle/update the global promo bar.
export async function updatePromo(payload) {
  const res = await fetch(`${API}/settings/promo`, {
    method: "PUT",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  return handle(res);
}

// Mark the current user as having left a review (one-time anti-spam lock).
export async function markReviewed() {
  const res = await fetch(`${API}/user/reviewed`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
  });
  return handle(res);
}

// Reset the review lock (QA/testing) so the embedded form can be resubmitted.
export async function resetReviewed() {
  const res = await fetch(`${API}/user/reviewed/reset`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
  });
  return handle(res);
}

// Start an anonymous guest trial session (1 free credit).
export async function guestLogin() {
  const res = await fetch(`${API}/auth/guest`, { method: "POST", credentials: "include", headers: authHeaders() });
  const data = await handle(res);
  setToken(data?.session_token); // keep the guest session alive on cookie-blocked PWAs
  return data;
}

// Email/password auth (runs alongside Google OAuth, issues the same session token).
export async function registerEmail(email, password, name) {
  const res = await fetch(`${API}/auth/register`, {
    method: "POST", credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, name }),
  });
  const data = await handle(res);
  setToken(data?.session_token);
  return data;
}

export async function loginEmail(email, password) {
  const res = await fetch(`${API}/auth/login`, {
    method: "POST", credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await handle(res);
  setToken(data?.session_token);
  return data;
}

// ---- Admin: grant credits, recent grants, event signups, registrations ----
export async function grantCredits(payload) {
  return apiPost("/admin/grant-credits", payload);
}
export async function getGrants() {
  return apiGet("/admin/grants");
}
export async function getEventSignups() {
  return apiGet("/admin/event-signups");
}
export async function getRegistrations() {
  return apiGet("/admin/registrations");
}
export async function getAdminSlipOverview() {
  return apiGet("/admin/slip-overview");
}
export async function purgeAllHistory() {
  return apiPost("/admin/purge-history");
}
// Admin: broadcast a Web Push announcement to all subscribed members.
export async function broadcastPush(payload) {
  return apiPost("/admin/push/broadcast", payload);
}

// Persist the onboarding-completed flag to the cloud account.
export async function markOnboarded() {
  return apiPost("/user/onboarded");
}

// Save the user's US state (+ optional ZIP) → precise local sales-tax rate (Real Money).
export async function saveLocation({ stateCode = "", zip = "", lat = null, lng = null, source = "manual" }) {
  return apiPost("/user/location", { state_code: stateCode, zip_code: zip, lat, lng, source });
}

// Clear the stored state (privacy toggle off → generic estimate).
export async function clearLocation() {
  return apiPost("/user/location", { state_code: "" });
}

// Save the applied max-budget filter on a saved search (so replays keep the narrowed view).
export async function updateHistoryFilter(txId, budget) {
  const res = await fetch(`${API}/credits/history/${txId}/filter`, {
    method: "PATCH",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ budget }),
  });
  return handle(res);
}

// Most-searched queries across all users — powers the Home "Trending" rail.
export async function getTrending() {
  return apiGet("/trending");
}

// ---- Community deals: native "Submit Alternative Website" ----
export async function submitCommunityDeal(payload) {
  return apiPost("/community/deals", payload);
}
export async function getCommunityDeals() {
  return apiGet("/admin/community-deals");
}
export async function reviewCommunityDeal(dealId, status) {
  return apiPatch(`/admin/community-deals/${dealId}`, { status });
}

// ---- Daily Recommendations Deck — persistent 24h lock (server-authoritative) ----
export async function getDailyDeckStatus() {
  return apiGet("/credits/daily-deck-status");
}
export async function advanceDailyDeck() {
  return apiPost("/credits/daily-deck-advance");
}

async function handle(res) {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { detail: text };
  }
  if (!res.ok) {
    const raw = data?.detail ?? data?.error;
    const msg = typeof raw === "string" ? raw : (raw?.message || JSON.stringify(raw) || `HTTP ${res.status}`);
    const err = new Error(msg);
    err.status = res.status;
    err.data = raw;
    // 402 = trial/limit reached. Distinguish guest trial vs subscription cap vs
    // Section 6 Deep Scan daily-credit exhaustion (handled locally by the caller).
    if (res.status === 402) {
      if (raw?.error === "limit_reached") window.dispatchEvent(new CustomEvent("open-upgrade", { detail: raw }));
      else if (!["out_of_credits", "vault_limit_reached", "daily_deck_locked", "guided_search_limit_reached"].includes(raw?.error)) window.dispatchEvent(new Event("guest-exhausted"));
    }
    // 403 out-of-credits on any paid action → mount the full-screen paywall interceptor.
    if (res.status === 403 && String(raw?.error || "").toLowerCase() === "insufficient_credits") window.dispatchEvent(new Event("open-paywall"));
    // 401 = session dropped/expired → force a clean redirect to the login shell (handled in AuthContext).
    if (res.status === 401) window.dispatchEvent(new Event("session-expired"));
    throw err;
  }
  return data;
}

export async function apiGet(path) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  return handle(res);
}

export async function apiPost(path, body, headers = {}) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json", ...headers }),
    body: body ? JSON.stringify(body) : undefined,
  });
  return handle(res);
}

export async function apiDelete(path) {
  const res = await fetch(`${API}${path}`, {
    method: "DELETE",
    credentials: "include",
    headers: authHeaders(),
  });
  return handle(res);
}

export async function apiPatch(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: body ? JSON.stringify(body) : undefined,
  });
  return handle(res);
}

// ---- Search (Inspect-a-Link / AI Web Search) ----
export async function updateStoreList(payload) { return apiPatch("/store/list", payload); }
export async function clearStoreList() { return apiDelete("/store/list"); }
export async function storeSearch(q) { return apiGet(`/store/search?q=${encodeURIComponent(q)}`); }
export async function storeCrawl(q) { return apiPost("/store/crawl", { q }); }
export async function webSearch(q, store, page) { return apiPost("/store/websearch", { q, store, page }); }
// Unified AI Web Search — intent-classified, tiered-cost, polymorphic result feed.
export async function unifiedSearch(q, priceMode = "full") { return apiPost("/v1/search/unified", { q, price_mode: priceMode }); }
export async function storeProductSearch(q, store, priceMode = "full") { return apiPost("/v1/search/stores", { q, store, price_mode: priceMode }); }
export async function portToLedger(items) { return apiPost("/store/websearch/port", { items }); }

// ---- Confirmed-purchase savings (Home dashboard "Cash Saved") ----
export async function confirmPurchase({ itemTitle, boughtPrice, maxPrice, url = "" }) {
  return apiPost("/purchases/confirm", { item_title: itemTitle, bought_price: boughtPrice, max_price: maxPrice, url });
}
export async function getPurchaseSummary() { return apiGet("/purchases/summary"); }

// ---- Wait list (save-and-check-later watchlist) ----
export async function addWait({ query, itemTitle, price, url, waitDays, category = "", region = "", target = null, timeframeDays = null }) {
  return apiPost("/waits/add", { query, item_title: itemTitle, price, url, wait_days: waitDays, category, region, target, timeframe_days: timeframeDays });
}
export async function listWaits() { return apiGet("/waits/list"); }
export async function deleteWait(waitId) { return apiDelete(`/waits/${waitId}`); }
// On app-open: fire one-time "good time to buy" alerts for due Waiting-On items.
export async function checkWaits() { return apiPost("/waits/check"); }
// On market open/refresh: evaluate active alerts vs today's regional cache (target/expiry).
export async function evaluateAlerts() { return apiPost("/waits/evaluate"); }

// ---- Slip — daily trading-card deal packs ----
export async function getSlipPacks() { return apiGet("/slip/packs"); }
export async function openSlipPack(niche) { return apiPost("/slip/packs/open", { niche }); }
export async function abandonSlipPack(packId) { return apiPost("/slip/packs/abandon", { pack_id: packId }); }
export async function slipCardAction(card, action) { return apiPost("/slip/cards/action", { card, action }); }
export async function getSlipCollection() { return apiGet("/slip/collection"); }
export async function getSlipUninterested() { return apiGet("/slip/uninterested"); }
export async function getSlipProfileStats() { return apiGet("/slip/profile-stats"); }
// Vault Card Upgrade Engine ("Deep Scan") + Create-a-Card (paste-a-link → custom Vault card).
export async function deepScanCard(cardId) { return apiPost("/slip/vault/deepscan", { card_id: cardId }); }
export async function createCustomCard(payload) { return apiPost("/slip/cards/create", payload); }
export async function createCardFromLink(url) { return apiPost("/slip/cards/create-from-link", { url }); }
export async function previewCardFromLink(url) { return apiPost("/slip/cards/preview-from-link", { url }); }
export async function discoverSearch(payload) { return apiPost("/search/discover", payload); }
export async function verifyLink(url) { return apiPost("/verify/link", { url }); }
// ---- Section 6: Outbound Redirect Router + Deep Trust Inspector credits ----
export async function getOutboundRedirect({ targetUrl, itemId = "", merchantName = "", itemTitle = "", price = null }) {
  const params = new URLSearchParams({ target_url: targetUrl, item_id: itemId, merchant_name: merchantName, item_title: itemTitle });
  if (price != null) params.set("price", price);
  return apiGet(`/redirect/outbound?${params.toString()}`);
}
export async function deepScan({ title, excludeDomain = "" }) { return apiPost("/verify/deep-scan", { title, exclude_domain: excludeDomain }); }
export async function getDeepScanStatus() { return apiGet("/credits/deep-scan-status"); }
// ---- Section 7: Public Share Engine + Referral & Credit Reward Engine ----
export async function createShareCard(payload) { return apiPost("/share/cards", { ...payload, origin_url: window.location.origin }); }
export async function getReferralCode() { return apiGet("/user/referral/code"); }
export async function claimReferral(referralCode) { return apiPost("/user/referral/claim", { referral_code: referralCode }); }
// ---- Section 8: Vault persistence, History, PDP reviews ----
export async function getVault() { return apiGet("/vault"); }
export async function saveToVault(payload) { return apiPost("/vault/save", payload); }
export async function removeFromVault(dealId) { return apiDelete(`/vault/save/${encodeURIComponent(dealId)}`); }
export async function logSwipe(payload) { return apiPost("/vault/swipe-log", payload); }
export async function getUserHistory() { return apiGet("/user/history"); }
export async function getSnaggedifyStats() { return apiGet("/user/snaggedify-stats"); }
export async function getPdpReviews(title) { return apiGet(`/pdp/reviews?title=${encodeURIComponent(title)}`); }
export async function getPdpLinkStatus({ url, title = "", domain = "", category = "" }) {
  const params = new URLSearchParams({ url, title, domain, category });
  return apiGet(`/pdp/link-status?${params.toString()}`);
}
export async function getAdminDisabledLinks() { return apiGet("/admin/disabled-links"); }
export async function reactivateDisabledLink(url) { return apiPost("/admin/disabled-links/reactivate", { url }); }

// ---- Retailer-Themed Search Workspace: Daily Spotlight + 3-Option Intent Engine ----
export async function getDailySpotlight() { return apiGet("/discover/spotlight"); }
export async function getGuidedSearchStatus() { return apiGet("/search/guided/status"); }
export async function guidedSearch({ retailerId, query, mode = "general", attributes = {} }) {
  return apiPost("/search/guided", { retailer_id: retailerId, query, mode, attributes });
}

// ---- Phase 3: in-app alert inbox + subscription usage caps ----
export async function getAlerts() { return apiGet("/alerts"); }
export async function getAlertsUnread() { return apiGet("/alerts/unread"); }
export async function markAlertsRead(alertIds = null) { return apiPost("/alerts/read", { alert_ids: alertIds }); }
export async function clearAlerts() { return apiDelete("/alerts"); }
export async function getUsageSummary() { return apiGet("/usage/summary"); }
